import { useCallback, useEffect } from 'react';

import { Input } from '@/_components';
import { snackbar } from '@/_shared';

import { FilterTypeSelector } from '../filter-type-selector';
import { FilterApplyTrigger } from '../filters-apply-trigger';
import { getFilterKey, getFilterValue, useFiltersState } from '../helper';
import type { AdvanceFilter } from '../types';

export function InputFilterWithRadios<T>({
  handleSubmitAdvFilters,
  ...perFilter
}: AdvanceFilter<T> & {
  handleSubmitAdvFilters: (v: AdvanceFilter<T>) => void;
}) {
  const { mainKey, subKey, subKey2, types, fieldType, selectedType, filterValue, applied } = perFilter;
  const {
    fromState: [fromValue, setFromValue],
    toState: [toValue, setToValue],
    typeState: [type, setType],
    showTwoValues,
    inputValueRequired,
    transition: [isPending, startTransition],
  } = useFiltersState(filterValue ?? '', types, selectedType);

  const handleSubmit = useCallback(() => {
    if (fromValue.length === 0 && inputValueRequired) {
      snackbar.error('Please enter the value to search');
      return;
    }

    if (toValue.length === 0 && showTwoValues && inputValueRequired) {
      snackbar.error('Please enter the second value to search');
      return;
    }

    if (type === null || typeof type === 'undefined' || type === '') {
      snackbar.error('Please select a filter type');
      return;
    }

    const selectedType = types.find((f) => f.value === type) ?? { name: '', value: '' };

    const key = getFilterKey(mainKey as string, subKey, subKey2);
    const { filter, filterValue } = getFilterValue(type, key, showTwoValues ? [fromValue, toValue] : [fromValue]);

    startTransition(() => {
      handleSubmitAdvFilters({
        ...perFilter,
        applied: true,
        filterValue,
        valueToApply: filter,
        selectedType,
      });
    });
  }, [
    fromValue,
    inputValueRequired,
    toValue,
    showTwoValues,
    type,
    mainKey,
    subKey,
    subKey2,
    startTransition,
    handleSubmitAdvFilters,
    perFilter,
    types,
  ]);

  useEffect(() => {
    if (!applied) {
      setFromValue('');
      setType(null);

      if (types.length === 1) {
        setType(types[0].value);
      } else {
        setType('');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applied]);

  return (
    <section className="flex flex-col">
      {inputValueRequired && (
        <div className="flex gap-1">
          <Input
            className="flex-1 placeholder:opacity-100"
            value={fromValue}
            type={fieldType}
            onChange={(e) => setFromValue(e.target.value)}
          />
          {showTwoValues && (
            <Input
              className="flex-1 placeholder:opacity-100"
              value={toValue}
              type={fieldType}
              onChange={(e) => setToValue(e.target.value)}
            />
          )}
        </div>
      )}

      {types.length > 1 && <FilterTypeSelector types={types} type={type} setType={setType} />}

      <FilterApplyTrigger handleSubmit={handleSubmit} isPending={isPending} />
    </section>
  );
}
